import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import './Auth.css';
import { BastionLockup } from './Brand';

function ResetPassword() {
  const { token } = useParams();
  const navigate = useNavigate();

  const [phase, setPhase] = useState('verifying');     // verifying | ready | submitting | done | invalid
  const [reason, setReason] = useState(null);          // 'not-found' | 'expired' | 'already-used'
  const [accountEmail, setAccountEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');

  // Verify the token once on mount. Idempotent — does not consume the token.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`/auth/reset-password/${token}/verify`);
        if (cancelled) return;
        if (res.data?.valid) {
          setAccountEmail(res.data.email || '');
          setPhase('ready');
        } else {
          setReason(res.data?.reason || 'not-found');
          setPhase('invalid');
        }
      } catch (err) {
        if (cancelled) return;
        setReason(err.response?.data?.reason || 'not-found');
        setPhase('invalid');
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    if (password !== confirm) {
      setError("Passwords don't match.");
      return;
    }

    setPhase('submitting');
    try {
      await axios.post(`/auth/reset-password/${token}`, { password });
      setPhase('done');
      // Bounce to login after a brief confirmation pause.
      setTimeout(() => navigate('/login', { replace: true }), 1800);
    } catch (err) {
      setError(err.response?.data?.error
            || err.response?.data?.errors?.[0]?.msg
            || 'Could not reset password. Please request a new link.');
      setPhase('ready');
    }
  };

  // ── Render variants ────────────────────────────────────────────────
  const heading = {
    verifying:  'Verifying link…',
    ready:      'Choose a new password',
    submitting: 'Choose a new password',
    done:       'Password updated',
    invalid:    'Link is no longer valid',
  }[phase];

  const subheading = {
    verifying:  'One moment.',
    ready:      accountEmail ? `Resetting password for ${accountEmail}` : '',
    submitting: accountEmail ? `Resetting password for ${accountEmail}` : '',
    done:       'Redirecting you to sign in…',
    invalid: {
      'expired':      'This reset link expired. Request a new one and try again.',
      'already-used': 'This reset link has already been used. Request a new one if you still need to reset.',
      'not-found':    "We couldn't find that reset link. It may have been mistyped, or it expired and was cleaned up.",
    }[reason] || 'Request a new link to continue.',
  }[phase];

  return (
    <div className="auth-container">
      <div className="auth-background">
        <div className="auth-gradient"></div>
      </div>

      <div className="auth-card">
        <div className="auth-header">
          <BastionLockup className="auth-lockup" variant="outline" markSize={64} />
          <h2>{heading}</h2>
          <p>{subheading}</p>
        </div>

        {error && <div className="error-message">{error}</div>}

        {(phase === 'ready' || phase === 'submitting') && (
          <form onSubmit={handleSubmit} className="auth-form">
            <div className="input-group">
              <label>New password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                placeholder="At least 6 characters"
                autoComplete="new-password"
                autoFocus
              />
            </div>
            <div className="input-group">
              <label>Confirm new password</label>
              <input
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
                minLength={6}
                placeholder="Re-enter password"
                autoComplete="new-password"
              />
            </div>
            <button type="submit" className="btn btn-primary btn-block" disabled={phase === 'submitting'}>
              {phase === 'submitting' ? 'Updating…' : 'Update password'}
            </button>
          </form>
        )}

        {phase === 'done' && (
          <div style={{
            background: 'rgba(34, 197, 94, 0.08)',
            border: '1px solid rgba(34, 197, 94, 0.25)',
            color: '#166534',
            padding: '14px 16px',
            borderRadius: 8,
            fontSize: 14,
            lineHeight: 1.55,
            marginBottom: 20,
            textAlign: 'center',
          }}>
            ✓ Your password has been reset. Redirecting…
          </div>
        )}

        {phase === 'invalid' && (
          <div className="auth-footer" style={{ marginTop: 8 }}>
            <p>
              <Link to="/forgot-password">Request a new reset link</Link>
            </p>
          </div>
        )}

        {phase !== 'invalid' && (
          <div className="auth-footer">
            <p><Link to="/login">← Back to sign in</Link></p>
          </div>
        )}
      </div>
    </div>
  );
}

export default ResetPassword;
